import type {Editor} from "@tiptap/core";
import {useCallback, useSyncExternalStore} from "react";
import type {VariableCatalogueStore} from "../editor/variableCatalogueStore";
import {DEFAULT_CONTENT_LANGUAGE} from "../model/resolution";
import type {VariableCatalogue} from "../model/types";
import {VARIABLE_NODE_NAME} from "../model/variableToken";

/** Module-level so an editor without the node returns the same object every render. */
const EMPTY_CATALOGUE: VariableCatalogue = {variables: [], preview: {language: DEFAULT_CONTENT_LANGUAGE}};

const NO_SUBSCRIPTION = () => () => undefined;

/** The node's store, or `undefined` when the variable node is not installed in this editor. */
export const variableCatalogueStoreOf = (editor: Editor): VariableCatalogueStore | undefined =>
    (editor.storage as unknown as Record<string, {store?: VariableCatalogueStore} | undefined>)[VARIABLE_NODE_NAME]
        ?.store;

/** The editor's current catalogue; re-renders when a host swaps it with `setVariableCatalogue`. */
export function useVariableCatalogue(editor: Editor): VariableCatalogue {
    const store = variableCatalogueStoreOf(editor);

    const subscribe = useCallback(
        (listener: () => void) => (store ? store.subscribe(listener) : NO_SUBSCRIPTION()),
        [store],
    );
    const get = useCallback(() => (store ? store.get() : EMPTY_CATALOGUE), [store]);

    return useSyncExternalStore(subscribe, get, get);
}

export default useVariableCatalogue;
